import { computed, toValue, type ComputedRef, type MaybeRefOrGetter } from 'vue'
import type {
  AgencyGroupNode,
  AgencyNode,
  AgencyStructureNode
} from '@gat-ship/types'
import {
  childrenOfGroup,
  filterInactiveAgencies,
  findRootGroup,
  partitionActiveChildren,
  sortNodesByTypeThenName
} from '../model/agency-structure-selectors'
import { useAgencyStructureContext } from './useAgencyStructure'

/** The sorted children of a group, split into active agencies and sub-groups. */
export interface AgencyGroupChildren {
  /** All direct children (active and inactive), sorted by `$type` then `name`. */
  children: ComputedRef<AgencyStructureNode[]>
  /** Active agency children, in sorted order. */
  agencies: ComputedRef<AgencyNode[]>
  /** Active sub-group children, in sorted order. */
  agencyGroups: ComputedRef<AgencyGroupNode[]>
}

/**
 * Reactive tree-node selectors over the injected agency structure — the MVVM
 * seam through which list components reach the pure `model/` selectors without
 * importing `model/` directly. Each accessor delegates to a pure function over
 * the current structure snapshot.
 */
export function useAgencyStructureSelectors() {
  const context = useAgencyStructureContext()

  const nodes = computed<AgencyStructureNode[]>(
    () => context?.agencyStructure.value ?? []
  )

  /** Inactive agencies for the deactivated-agencies list. */
  const inactiveAgencies = computed(() => filterInactiveAgencies(nodes.value))

  /** The root agency group, or `undefined` when the structure is empty. */
  const rootGroup = computed(() => findRootGroup(nodes.value))

  /**
   * The sorted children of `groupId` split into active agencies and sub-groups.
   * Accepts a ref/getter so a recursive list item stays reactive to its node id.
   */
  function groupChildren(
    groupId: MaybeRefOrGetter<string>
  ): AgencyGroupChildren {
    const children = computed(() =>
      sortNodesByTypeThenName(childrenOfGroup(nodes.value, toValue(groupId)))
    )
    const partition = computed(() => partitionActiveChildren(children.value))
    return {
      children,
      agencies: computed(() => partition.value.agencies),
      agencyGroups: computed(() => partition.value.agencyGroups)
    }
  }

  return { inactiveAgencies, rootGroup, groupChildren }
}
